import { useState, useMemo } from 'react'

export type Zone =
  | 'Kitchen Pantry'
  | 'Refrigerator'
  | 'Freezer'
  | 'Bathroom'
  | 'Cleaning Closet'
export type Status = 'Good' | 'Warning' | 'Critical' | 'Expired'

export interface InventoryItem {
  id: string
  name: string
  category: string
  quantity: number
  unit: string
  maxQuantity: number
  zone: Zone
  expiryDate: string
  lastMoved: string
  photoUrl?: string
  status: Status
  notes?: string
}

const INITIAL_INVENTORY: InventoryItem[] = [
  {
    id: '1',
    name: 'Butterbeer (Juneau Brew)',
    category: 'Wizarding Beverages',
    quantity: 3,
    unit: 'bottles',
    maxQuantity: 6,
    zone: 'Refrigerator',
    expiryDate: '2025-03-15',
    lastMoved: '2025-02-03',
    photoUrl:
      'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'Brewed by the Three Broomsticks of Alaska. Warming charm applied.',
  },
  {
    id: '2',
    name: 'Hippogriff Eggs (Denali)',
    category: 'Magical Creatures',
    quantity: 4,
    unit: 'eggs',
    maxQuantity: 12,
    zone: 'Refrigerator',
    expiryDate: '2025-02-20',
    lastMoved: '2025-02-01',
    photoUrl:
      'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400&h=400&fit=crop',
    status: 'Warning',
    notes: 'Handle with respect! Bow before cooking.',
  },
  {
    id: '3',
    name: 'Pumpkin Pasties',
    category: 'Enchanted Sweets',
    quantity: 2,
    unit: 'boxes',
    maxQuantity: 4,
    zone: 'Kitchen Pantry',
    expiryDate: '2025-02-10',
    lastMoved: '2025-02-02',
    photoUrl:
      'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'From the Hogwarts Express trolley witch. Accio snacks!',
  },
  {
    id: '4',
    name: 'Chocolate Frogs (Aurora Ed.)',
    category: 'Enchanted Sweets',
    quantity: 5,
    unit: 'frogs',
    maxQuantity: 12,
    zone: 'Kitchen Pantry',
    expiryDate: '2026-06-01',
    lastMoved: '2025-01-15',
    photoUrl:
      'https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'Northern Lights collector cards inside. Immobulus before opening!',
  },
  {
    id: '5',
    name: 'Gillyweed (Kodiak Harvest)',
    category: 'Spell Components',
    quantity: 1,
    unit: 'bundles',
    maxQuantity: 5,
    zone: 'Refrigerator',
    expiryDate: '2025-02-08',
    lastMoved: '2025-01-20',
    photoUrl:
      'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=400&fit=crop',
    status: 'Critical',
    notes: 'For underwater expeditions in Alaskan fjords. Lasts one hour.',
  },
  {
    id: '6',
    name: 'Dragon Steak (Ridgeback)',
    category: 'Magical Creatures',
    quantity: 3,
    unit: 'lbs',
    maxQuantity: 6,
    zone: 'Freezer',
    expiryDate: '2025-06-15',
    lastMoved: '2025-01-28',
    photoUrl:
      'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'Alaskan Norwegian Ridgeback. Incendio recommended for cooking.',
  },
  {
    id: '7',
    name: 'Skele-Gro Tonic',
    category: 'Potions & Elixirs',
    quantity: 2,
    unit: 'vials',
    maxQuantity: 6,
    zone: 'Bathroom',
    expiryDate: '2027-01-01',
    lastMoved: '2025-02-01',
    photoUrl:
      'https://images.unsplash.com/photo-1584556812952-905ffd0c611a?w=400&h=400&fit=crop',
    status: 'Warning',
    notes: 'For bone regrowth. Tastes dreadful but works wonders!',
  },
  {
    id: '8',
    name: 'Wolfsbane Potion',
    category: 'Potions & Elixirs',
    quantity: 1,
    unit: 'cauldrons',
    maxQuantity: 3,
    zone: 'Bathroom',
    expiryDate: '2025-02-28',
    lastMoved: '2025-01-15',
    photoUrl:
      'https://images.unsplash.com/photo-1559650656-5d1d361ad10e?w=400&h=400&fit=crop',
    status: 'Warning',
    notes: 'Essential for full moon nights in the Alaskan wilderness.',
  },
  {
    id: '9',
    name: 'Floo Powder (Anchorage)',
    category: 'Spell Components',
    quantity: 1,
    unit: 'pouches',
    maxQuantity: 3,
    zone: 'Cleaning Closet',
    expiryDate: '2028-01-01',
    lastMoved: '2025-01-10',
    photoUrl:
      'https://images.unsplash.com/photo-1585421514738-01798e348b17?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'Speak clearly! Connected to Alaskan Floo Network.',
  },
  {
    id: '10',
    name: 'Polyjuice Ingredients',
    category: 'Potions & Elixirs',
    quantity: 1,
    unit: 'kits',
    maxQuantity: 2,
    zone: 'Cleaning Closet',
    expiryDate: '2026-01-01',
    lastMoved: '2025-01-05',
    photoUrl:
      'https://images.unsplash.com/photo-1582735689369-4fe89db7114c?w=400&h=400&fit=crop',
    status: 'Critical',
    notes: 'Lacewing flies running low! Takes a month to brew.',
  },
  {
    id: '11',
    name: 'Treacle Tart',
    category: 'Enchanted Sweets',
    quantity: 1,
    unit: 'tarts',
    maxQuantity: 3,
    zone: 'Refrigerator',
    expiryDate: '2025-02-12',
    lastMoved: '2025-02-01',
    photoUrl:
      'https://images.unsplash.com/photo-1589985270826-4b7bb135bc9d?w=400&h=400&fit=crop',
    status: 'Critical',
    notes: "Harry's favorite! Made with Alaskan golden syrup.",
  },
  {
    id: '12',
    name: 'Firewhisky (Fairbanks)',
    category: 'Wizarding Beverages',
    quantity: 2,
    unit: 'bottles',
    maxQuantity: 4,
    zone: 'Kitchen Pantry',
    expiryDate: '2030-12-01',
    lastMoved: '2025-01-20',
    photoUrl:
      'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'Aged in enchanted barrels. Warms you in -40° weather!',
  },
  {
    id: '13',
    name: 'Bertie Botts Beans',
    category: 'Enchanted Sweets',
    quantity: 8,
    unit: 'boxes',
    maxQuantity: 12,
    zone: 'Kitchen Pantry',
    expiryDate: '2026-02-08',
    lastMoved: '2025-02-03',
    photoUrl:
      'https://images.unsplash.com/photo-1571771894821-ce9b6c11b08e?w=400&h=400&fit=crop',
    status: 'Good',
    notes: 'Every Flavour means EVERY flavour. Salmon flavor is popular here!',
  },
  {
    id: '14',
    name: 'Phoenix Feather Tea',
    category: 'Wizarding Beverages',
    quantity: 2,
    unit: 'tins',
    maxQuantity: 5,
    zone: 'Kitchen Pantry',
    expiryDate: '2025-02-14',
    lastMoved: '2025-02-01',
    photoUrl:
      'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=400&h=400&fit=crop',
    status: 'Warning',
    notes: 'Healing properties. Harvested ethically from Fawkes descendants.',
  },
  {
    id: '15',
    name: 'Cauldron Cakes',
    category: 'Enchanted Sweets',
    quantity: 3,
    unit: 'cakes',
    maxQuantity: 8,
    zone: 'Kitchen Pantry',
    expiryDate: '2025-02-18',
    lastMoved: '2025-01-28',
    photoUrl:
      'https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=400&h=400&fit=crop',
    status: 'Warning',
    notes: 'Self-stirring frosting! From Honeydukes Sitka branch.',
  },
]

export function useInventory() {
  const [inventory, setInventory] = useState<InventoryItem[]>(INITIAL_INVENTORY)

  const getStats = useMemo(() => {
    const totalItems = inventory.length
    const lowStock = inventory.filter(
      (i) => i.status === 'Warning' || i.status === 'Critical',
    ).length
    const critical = inventory.filter((i) => i.status === 'Critical').length
    const expiringSoon = inventory.filter((i) => {
      const days = Math.ceil(
        (new Date(i.expiryDate).getTime() - new Date().getTime()) /
          (1000 * 3600 * 24),
      )
      return days <= 7 && days > 0
    }).length

    return { totalItems, lowStock, critical, expiringSoon }
  }, [inventory])

  const getByZone = (zone: Zone) => {
    return inventory.filter((item) => item.zone === zone)
  }

  const getExpiringSoon = () => {
    return inventory
      .filter((i) => {
        const days = Math.ceil(
          (new Date(i.expiryDate).getTime() - new Date().getTime()) /
            (1000 * 3600 * 24),
        )
        return days <= 14
      })
      .sort(
        (a, b) =>
          new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime(),
      )
  }

  const searchItems = (query: string) => {
    if (!query) return []
    const lowerQuery = query.toLowerCase()
    return inventory.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerQuery) ||
        item.zone.toLowerCase().includes(lowerQuery) ||
        item.category.toLowerCase().includes(lowerQuery),
    )
  }

  const getShoppingList = () => {
    return inventory
      .filter((item) => {
        const stockRatio = item.quantity / item.maxQuantity
        return (
          stockRatio < 0.5 ||
          item.status === 'Critical' ||
          item.status === 'Warning'
        )
      })
      .map((item) => ({
        ...item,
        suggestedQuantity: item.maxQuantity - item.quantity,
        priority:
          item.status === 'Critical'
            ? 'High'
            : item.status === 'Warning'
              ? 'Medium'
              : 'Low',
      }))
      .sort((a, b) => {
        const priorities = { High: 3, Medium: 2, Low: 1 }
        return (
          priorities[b.priority as keyof typeof priorities] -
          priorities[a.priority as keyof typeof priorities]
        )
      })
  }

  const addItem = (
    item: Omit<InventoryItem, 'id' | 'status' | 'lastMoved'>,
  ) => {
    const stockRatio = item.quantity / item.maxQuantity
    const status: Status =
      stockRatio < 0.25 ? 'Critical' : stockRatio < 0.5 ? 'Warning' : 'Good'

    const newItem: InventoryItem = {
      ...item,
      id: Math.random().toString(36).substr(2, 9),
      status,
      lastMoved: new Date().toISOString().split('T')[0],
    }
    setInventory((prev) => [...prev, newItem])
  }

  const updateItem = (id: string, updates: Partial<InventoryItem>) => {
    setInventory((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    )
  }

  const deleteItem = (id: string) => {
    setInventory((prev) => prev.filter((item) => item.id !== id))
  }

  return {
    inventory,
    getStats,
    getByZone,
    getShoppingList,
    getExpiringSoon,
    searchItems,
    addItem,
    updateItem,
    deleteItem,
  }
}
